import React, { useState } from 'react'
import { Controller, Control, useWatch } from 'react-hook-form'
import {
  CustomDatePickerControlled,
  QuestionCard,
  CustomCheckboxInput,
  View,
  ActionButton,
  SimpleText,
  Image,
  XPDatePicker,
  Label,
} from '@my/ui'
//import DateTimePickerModal from 'react-native-modal-datetime-picker'

import {
  educationalBackgroundOptions,
  employmentStatusOptions,
  employmentType,
  confirmationOptions,
  pronounsItems,
  relationshipStatusOptions,
  April2026UpdatedOptions
} from '../data'
import images from '../../../assets/images'
import { US_STATES_ABBREVIATIONS } from '../states'
import { DropdownField, InputField } from '../helpers/Helper'
import { color, formatDate } from '@my/util'

interface ClientIdentificationAndBackgroundProps {
  control: Control<any>
  registerWithLabel: Function
}

export const ClientIdentificationAndBackground: React.FC<
  ClientIdentificationAndBackgroundProps
> = ({ control, registerWithLabel }) => {
  const [isDatePickerVisible, setDatePickerVisibility] = useState(false)
  const hasDependentsFormValue = useWatch({ name: 'Has Dependents'});
  const hasDependents = typeof hasDependentsFormValue === 'boolean'
  ? hasDependentsFormValue
  : ![undefined, null, false, 'No dependents'].includes(hasDependentsFormValue)
  const employmentStatusFormValue = useWatch({ name: 'Employment Status'});
  const employmentStatus = typeof employmentStatusFormValue === 'boolean'
  ? employmentStatusFormValue
  : ![undefined, null, false, 'Student (no income)', 'Retired', 'Unemployed'].includes(employmentStatusFormValue)

  const showDatePicker = () => {
    setDatePickerVisibility(true)
  }

  const hideDatePicker = () => {
    setDatePickerVisibility(false)
  }

  const handleConfirm = (date) => {
    console.warn('A date has been picked: ', date)
    hideDatePicker()
  }
  return (
    <>
      {/* Question Card for Full Name and Contact Information */}
      <QuestionCard questionGroupTitle="What is your full name and contact information?">
        <InputField
          control={control}
          name="Full name"
          placeholder="Full Name"
          icon={images.profile2}
          rules={{ required: true }} // Required field validation
        />
        <InputField
          control={control}
          name="Pronouns"
          icon={images.pronouns}
          placeholder="Pronouns"
          //items={pronounsItems} // Dropdown options for pronouns
        />
        <InputField
          control={control}
          name="Email"
          placeholder="Email Address"
          icon={images.message}
          rules={{ pattern: /^\S+@\S+$/i }} // Email format validation
        />
        <InputField
          control={control}
          name="Mobile number"
          placeholder="Mobile number"
          icon={images.AdvisorMobile}
          rules={{ minLength: 6, maxLength: 18 }} // Length validation for mobile number
        />
        <DropdownField
          control={control}
          name="State of Residency"
          icon={images.AdvisorLocation}
          placeholder="U.S. State/Territory of Residence"
          items={US_STATES_ABBREVIATIONS} // Dropdown options for U.S. states
          native
        />
      </QuestionCard>

      {/* Question Card for Date of Birth and Expected Retirement Age */}
      <QuestionCard questionGroupTitle="What is your date of birth?">
        <Controller
          name="Date of Birth"
          control={control}
          rules={{ required: true }} // Required field validation
          render={({ field: { onChange, name, value }, fieldState: { error } }) => (
            <>
              <XPDatePicker
                  onChange={(date) => onChange(date)}
                  name="Date of Birth"
                  value={value}
                  placeHolder="Date of Birth"
                  icon={images.AdvisorGender}
                  showIcon={true}
                  tintColor={'rgb(133, 193, 157)'}
                  color={color.black}
                  onPress={showDatePicker}
                  borderColor={error ? color.lightRed : color.dullGrey}
                  isVisible={isDatePickerVisible}
                  onConfirm={handleConfirm}
                  onCancel={hideDatePicker}
                  actionButtonProps={{
                    unstyled: true,
                    width: '100%',
                    height: 40,
                    borderWidth: 2,
                    alignItems: 'center',
                    paddingHorizontal: 15,
                    flexDirection: 'row',
                    borderRadius: 4
                  }}
                  /*nativeExtraContent={<Image
                    style={{ height: 20, width: 20 }}
                    src={images.AdvisorGender}
                    tintColor={error ? color.lightRed : color.black}
                    resizeMode="contain"
                  />}*/
                  webNative
                  simpleTextProps={{color: error ? color.lightRed : color.black}}
              />
            </>
          )}
        />
        {/* <InputField
          control={control}
          name="Desired Retirement Age"
          placeholder="Desired Retirement Age"
          icon={images.retirementAge}
          rules={{ pattern: /^[0-9]{0,2}$/i }} // Numeric input validation
        /> */}
      </QuestionCard>

      {/* Question Card for Relationship Status */}
      <QuestionCard questionGroupTitle="What is your relationship status?">
        <DropdownField
          control={control}
          name="Relationship Status"
          items={April2026UpdatedOptions['Client Info'].relationshipStatus} // Checkbox options for relationship status
          native
        />
      </QuestionCard>

      {/* Question Card for Tax Filing Status */}
      <QuestionCard questionGroupTitle="What is your tax filing marital status?">
        <DropdownField
          control={control}
          name="Tax Filing Marital Status"
          items={April2026UpdatedOptions['Client Info'].taxFilingStatus}
          native
        />
      </QuestionCard>

      {/* Question Card for Dependents */}
      <QuestionCard questionGroupTitle="Do you have any dependents?">
        <DropdownField
          control={control}
          name="Has Dependents"
          items={April2026UpdatedOptions['Client Info'].dependentsType}
          native
        />
        {/* hasDependents && <InputField
          control={control}
          name="Has Dependents - Number of Dependents"
          placeholder="How many dependents do you have?"
        /> */}
        {hasDependents && <>
          <Label>Are these dependents reflected in your annual tax filing?</Label>
          <CustomCheckboxInput
            options={confirmationOptions} // Checkbox options for dependents
            control={control}
            name="Has Dependents - Reflected in Tax Filing?"
          />
        </>}
      </QuestionCard>

      {/* Question Card for Employment Status */}
      <QuestionCard questionGroupTitle="What is your current employment status?">
        <DropdownField
          control={control}
          name="Employment Status"
          items={April2026UpdatedOptions['Client Info'].employmentTypeExpanded}
          native
        />
      </QuestionCard>

      {/* Question Card for Employment Type */}
      {employmentStatus && <QuestionCard questionGroupTitle="Please describe your job or business.">
        {/* <CustomCheckboxInput
          options={employmentType} // Checkbox options for employment type
          control={control}
          name="Employment Type"
          disabled={!employmentStatus}
        /> */}
        <InputField
          control={control}
          name="Employment/Business Description"
          placeholder="Employment/Business Description" // Updated placeholder for clarity
          //rules={{ required: true }} // Required field validation
          height={100}
          multiline={true} // Allow multiline input
          disabled={!employmentStatus}
        />
      </QuestionCard>}
      {/* Question Card for Additional Career Training */}
      {/* {employmentStatus && <QuestionCard questionGroupTitle="Does your career require additional training or education?">
        <CustomCheckboxInput
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]} // Checkbox options for additional training
          control={control}
          name="Additional Career Training/Education"
        />
      </QuestionCard>} */}

      {/* Question Card for Educational Background */}
      <QuestionCard questionGroupTitle="What is your educational background? ">
        <CustomCheckboxInput
          options={educationalBackgroundOptions} // Checkbox options for educational background
          control={control}
          name="Education Background Description"
        />
      </QuestionCard>

    </>
  )
}
